import { create } from "zustand";
import {
  ProductEcomQueueAction,
  ProductEcomQueueActionInput,
  ProductEcomQueueEnqueueResponse,
  ProductEcomQueueJobQuery,
  ProductEcomQueueJobView,
  ProductEcomQueueState,
} from "../types/product_ecom_queue";
import { useFetcher } from "./fetcher";
import { Paging } from "../types/paging";
import { sortQueryFromOrderByRecord } from "../utils/query";

const DEFAULT_LIMIT = 20;

export const useProductEcomQueueStore = create<ProductEcomQueueState>(
  (set, get) =>
    ({
      jobs: [],
      total: null,
      query: null,
      limit: DEFAULT_LIMIT,
      enqueue: async (
        action: ProductEcomQueueAction,
        input: ProductEcomQueueActionInput
      ): Promise<ProductEcomQueueEnqueueResponse | null> => {
        const res = await useFetcher
          .getState()
          .omsDsFetcher.post<ProductEcomQueueEnqueueResponse>(
            `product-ecoms/queue/${action}`,
            input
          );
        return res?.data ?? null;
      },
      search: async (query?: ProductEcomQueueJobQuery, fetch?: boolean) => {
        set(() => ({ query: query ?? null }));
        if (fetch !== false) {
          await get().fetch(true);
        }
      },
      fetch: async (
        reset: boolean
      ): Promise<Paging<ProductEcomQueueJobView> | null> => {
        const { jobs, limit, query, total } = get();
        if (!reset && total !== null && total <= jobs.length) {
          return null;
        }
        const sort = sortQueryFromOrderByRecord(query?.order_by);
        const params: any = query
          ? {
              ...query,
              order_by: undefined,
              sort,
              limit,
              offset: reset ? 0 : jobs.length,
            }
          : { limit, offset: reset ? 0 : jobs.length };
        const res = await useFetcher
          .getState()
          .omsDsFetcher.get<Paging<ProductEcomQueueJobView>>(
            "product-ecoms/queue/jobs",
            {
              params,
              paramsSerializer: { indexes: null },
            }
          );
        set(() => ({
          jobs: [...(reset ? [] : jobs), ...(res?.data?.items ?? [])],
          total: res?.data?.total ?? null,
        }));
        return res?.data ?? null;
      },
      retry: async (jobId: number): Promise<boolean> => {
        const res = await useFetcher
          .getState()
          .omsDsFetcher.post(`product-ecoms/queue/jobs/${jobId}/retry`);
        return !!res && res.status >= 200 && res.status < 300;
      },
      clear: () => {
        set(() => ({ jobs: [], total: null, query: null }));
      },
    } as ProductEcomQueueState)
);
